import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrashIcon, Trash2Icon, ClockIcon } from 'lucide-react';
import { HistoryEntry } from '../hooks/useCalculatorHistory';
import { formatCurrency, formatNumber } from '../utils/leasingFactors';
interface HistoryViewProps {
  history: HistoryEntry[];
  onRemoveEntry: (id: string) => void;
  onClearHistory: () => void;
}
function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(timestamp));
}
export function HistoryView({
  history,
  onRemoveEntry,
  onClearHistory
}: HistoryViewProps) {
  if (history.length === 0) {
    return <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200 text-center" data-id="element-25">
        <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4" data-id="element-26">
          <ClockIcon className="w-8 h-8 text-stone-400" data-id="element-27" />
        </div>
        <h3 className="text-lg font-semibold text-stone-700 mb-2" data-id="element-28">
          Keine Berechnungen gespeichert
        </h3>
        <p className="text-stone-500 text-sm" data-id="element-29">
          Gespeicherte Berechnungen erscheinen hier.
        </p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-30">
      {/* Header with Clear Button */}
      <div className="flex items-center justify-between" data-id="element-31">
        <p className="text-sm text-stone-600" data-id="element-32">
          {history.length}{' '}
          {history.length === 1 ? 'Berechnung' : 'Berechnungen'}
        </p>
        <button onClick={onClearHistory} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors" data-id="element-33">
          <Trash2Icon className="w-4 h-4" data-id="element-34" />
          Alle löschen
        </button>
      </div>

      {/* History List */}
      <div className="space-y-3" data-id="element-35">
        <AnimatePresence initial={false} data-id="element-36">
          {history.map((entry, index) => <motion.div key={entry.id} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20,
          height: 0
        }} transition={{
          duration: 0.2,
          delay: index * 0.03
        }} className="bg-white rounded-xl p-4 shadow-sm border border-stone-200" data-id="element-37">
              <div className="flex items-start justify-between gap-4" data-id="element-38">
                <div className="flex-1 min-w-0" data-id="element-39">
                  <div className="flex items-baseline gap-3 mb-2" data-id="element-40">
                    <span className="text-2xl font-bold text-stone-900" data-id="element-41">
                      {formatCurrency(entry.monthlyRate)}
                    </span>
                    <span className="text-sm text-stone-500" data-id="element-42">/ Monat</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-600" data-id="element-43">
                    <span data-id="element-44">NAW: {formatCurrency(entry.naw)}</span>
                    <span data-id="element-45">{entry.term} Monate</span>
                    <span data-id="element-46">Faktor: {formatNumber(entry.factor)}%</span>
                  </div>
                  <p className="mt-2 text-xs text-stone-400" data-id="element-47">
                    {formatDate(entry.timestamp)}
                  </p>
                </div>
                <button onClick={() => onRemoveEntry(entry.id)} className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Berechnung löschen" data-id="element-48">
                  <TrashIcon className="w-4 h-4" data-id="element-49" />
                </button>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </div>;
}